import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { translationApi } from '../lib/api';

// Language display names
const LANG_NAMES = {
  en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch',
  it: 'Italiano', pt: 'Português', hi: 'हिन्दी', bn: 'বাংলা',
  zh: '中文', pa: 'ਪੰਜਾਬੀ'
};

export const TranslatableMessage = ({ message, isOwnMessage }) => {
  const { t, language } = useLanguage();
  
  // Translation state
  const [showOriginal, setShowOriginal] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(null);
  const [sourceLang, setSourceLang] = useState(message.lang || 'en');
  
  // Check if translation is needed
  const messageLang = message.lang || 'en';
  const needsTranslation = messageLang !== language;
  
  // Fetch translation when needed
  useEffect(() => {
    if (needsTranslation && !translatedText && !isTranslating && !translationError) {
      fetchTranslation();
    }
  }, [message.id, language, needsTranslation]);
  
  // Reset translation when language changes
  useEffect(() => {
    setTranslatedText(null);
    setTranslationError(null);
    setShowOriginal(false);
  }, [language]);
  
  const fetchTranslation = async () => {
    setIsTranslating(true);
    setTranslationError(null);
    try {
      const response = await translationApi.translateMessage(message.id, language);
      const data = response.data;
      
      if (data.is_original) {
        setTranslatedText(null);
      } else {
        setTranslatedText(data.translated_text);
        setSourceLang(data.source_lang || message.lang || 'en');
      }
    } catch (err) {
      console.error('Message translation error:', err);
      // Don't show error to user, just show original
      setTranslationError(err.response?.data?.detail || 'Translation unavailable');
    } finally {
      setIsTranslating(false);
    }
  };
  
  // Get display text
  const displayText = (!needsTranslation || showOriginal || !translatedText) 
    ? message.content 
    : translatedText;
  
  // Format timestamp
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      className={`max-w-[80%] ${isOwnMessage ? 'ml-auto' : 'mr-auto'}`}
      data-testid={`message-${message.id}`}
    >
      <div
        className={`rounded-2xl px-4 py-2 ${
          isOwnMessage
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-accent rounded-bl-md'
        }`}
      >
        {/* Translation loading indicator */}
        {isTranslating && (
          <div className="flex items-center gap-1.5 text-[10px] opacity-70 mb-1">
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
            <span>{t('translating') || 'Translating...'}</span>
          </div>
        )}
        
        <p className={`text-sm ${isTranslating ? 'opacity-60' : ''}`}>
          {displayText}
        </p>

        {/* Media attachments */}
        {message.media && message.media.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.media.map((m, i) => (
              <div key={i}>
                {m.type === 'image' && (
                  <img src={m.data} alt={m.caption || ''} className="rounded-lg max-h-48 max-w-full cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const viewer = document.createElement('div');
                      viewer.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.95);display:flex;align-items:center;justify-content:center;cursor:pointer';
                      viewer.onclick = () => viewer.remove();
                      const img = document.createElement('img');
                      img.src = m.data;
                      img.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;padding:16px';
                      viewer.appendChild(img);
                      document.body.appendChild(viewer);
                    }} />
                )}
                {m.caption && <p className="text-[10px] opacity-70 mt-0.5">{m.caption}</p>}
              </div>
            ))}
          </div>
        )}
        
        {/* See Original Toggle */}
        {needsTranslation && translatedText && !isTranslating && (
          <button
            onClick={() => setShowOriginal(!showOriginal)}
            className={`flex items-center gap-1 text-[10px] mt-1 transition-colors ${
              isOwnMessage 
                ? 'text-primary-foreground/70 hover:text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`message-toggle-original-${message.id}`}
          >
            {showOriginal ? (
              <>
                <EyeOff className="w-2.5 h-2.5" />
                <span>{t('see_translation') || 'See translation'}</span>
              </>
            ) : (
              <>
                <Eye className="w-2.5 h-2.5" />
                <span>{t('see_original') || 'See original'} ({LANG_NAMES[sourceLang] || sourceLang})</span>
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Timestamp */}
      <p className={`text-[10px] text-muted-foreground mt-0.5 ${
        isOwnMessage ? 'text-right' : 'text-left'
      }`}>
        {formatTime(message.created_at)}
      </p>
    </div>
  );
};

export default TranslatableMessage;
